import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { UseHistory } from '@/views/editor/composable/useHistory.ts'
import { useKeyCountManager } from '@/views/editor/composable/useEditorHelpers.ts'

/**
 * 브라우저 네이티브 Undo 동작을 재현하는 히스토리 관리자
 * 
 * 저장 트리거:
 * 1. 타이핑 pause (1초)
 * 2. 단어 경계 (스페이스, 구두점)
 * 3. Enter 키
 * 4. 삭제 방향 전환
 */
export class TypingHistoryManager {
	private typingTimer: ReturnType<typeof setTimeout> | null = null
	private lastSaveTime: number = 0
	private lastDeleteDirection: 'backspace' | 'delete' | null = null
	private lastInputMode: 'typing' | 'deleting' | null = null
	private history: UseHistory

	private readonly TYPING_PAUSE = 1000
	private readonly MIN_SAVE_INTERVAL = 100
	private readonly WORD_BOUNDARIES = [' ', '.', ',', '!', '?', ';', ':', '\n']

	constructor(history: UseHistory) {
		this.history = history
	}

	async onInput(event: InputEvent): Promise<void> {
		const { isUndoRedoInProgress, redoStack } = storeToRefs(useEditorStore())
		if (isUndoRedoInProgress.value) {
			return
		}
		
		const keyCountManager = useKeyCountManager()
		if (keyCountManager.isComposing.value) {
			return
		}

		if (redoStack.value.length > 0) {
			redoStack.value = []
		}
		
		const isDeleteInput = event.inputType?.includes('delete')
		
		if (!isDeleteInput) {
			this.lastInputMode = 'typing'
		}
		
		this.clearTimer()

		if (event.data && this.isWordBoundary(event.data)) {
			await this.saveHistory('Word boundary', true)
			return
		}
		
		if (!event.data || event.data === 'null') {
			const sel = window.getSelection()
			if (sel && sel.rangeCount > 0) {
				const range = sel.getRangeAt(0)
				const node = range.startContainer
				if (node.nodeType === Node.TEXT_NODE && node.textContent) {
					const charBefore = node.textContent[range.startOffset - 1]
					if (charBefore && this.isWordBoundary(charBefore)) {
						await this.saveHistory('Word boundary', true)
						return
					}
				}
			}
		}

		this.typingTimer = setTimeout(async () => {
			await this.saveHistory('Typing pause')
			this.typingTimer = null
		}, this.TYPING_PAUSE)
	}

	async onEnter(): Promise<void> {
		this.clearTimer()
		await this.history.push()
		this.lastSaveTime = Date.now()
	}

	async onDelete(key: 'Backspace' | 'Delete'): Promise<void> {
		if (this.isFunctionEditing()) return

		const direction = key === 'Backspace' ? 'backspace' : 'delete'

		if (this.lastInputMode === 'typing') {
			this.clearTimer()
			await this.history.push()
			this.lastSaveTime = Date.now()
			this.lastInputMode = 'deleting'
		} else if (this.lastDeleteDirection && this.lastDeleteDirection !== direction) {
			this.clearTimer()
			await this.history.push()
			this.lastSaveTime = Date.now()
		}

		this.lastDeleteDirection = direction
		
		this.clearTimer()
		this.typingTimer = setTimeout(async () => {
			if (this.isFunctionEditing()) {
				this.typingTimer = null
				return
			}
			await this.history.push()
			this.lastSaveTime = Date.now()
			this.typingTimer = null
		}, this.TYPING_PAUSE)
	}

	async onRegularKey(): Promise<void> {
		if (this.isFunctionEditing()) return

		if (this.lastInputMode === 'deleting') {
			this.clearTimer()
			await this.history.push()
			this.lastSaveTime = Date.now()
		}

		const sel = window.getSelection()
		if (sel && !sel.isCollapsed && this.typingTimer) {
			this.clearTimer()
			await this.history.push()
			this.lastSaveTime = Date.now()
		}
		
		this.lastInputMode = 'typing'
		this.lastDeleteDirection = null
	}

	private async saveHistory(reason: string, isWordBoundary: boolean = false): Promise<void> {
		const now = Date.now()

		if (!isWordBoundary && now - this.lastSaveTime < this.MIN_SAVE_INTERVAL) {
			return
		}

		const { isUndoRedoInProgress, isFunctionActive, cellTarget } = storeToRefs(useEditorStore())
		if (isUndoRedoInProgress.value) {
			return
		}
		
		// Skip saving during function editing or in function cells
		if (isFunctionActive.value || cellTarget.value?.dataset?.type === 'function') {
			return
		}
		
		await this.history.push()
		this.lastSaveTime = now
	}

	private isFunctionEditing(): boolean {
		const { isFunctionActive, cellTarget } = storeToRefs(useEditorStore())
		return isFunctionActive.value || cellTarget.value?.dataset?.type === 'function'
	}

	private isWordBoundary(char: string): boolean {
		return this.WORD_BOUNDARIES.includes(char)
	}

	private clearTimer(): void {
		if (this.typingTimer) {
			clearTimeout(this.typingTimer)
			this.typingTimer = null
		}
	}

	async saveCurrentState(): Promise<void> {
		const hadPendingTimer = this.typingTimer !== null
		this.clearTimer()
		
		const { undoStack, redoStack } = storeToRefs(useEditorStore())
		
		// redoStack이 있으면 Undo 직후 상태이므로 저장 스킵
		// 단, 미저장 타이핑이 있는 경우(hadPendingTimer)는 저장 진행
		// → history.push()가 redoStack을 비워 redo를 막음
		if (redoStack.value.length > 0 && !hadPendingTimer) {
			return
		}
		
		// 최근 undoStack 항목이 테이블 작업이면 중복 저장 방지
		const lastState = undoStack.value[undoStack.value.length - 1]
		
		if (lastState?.metadata?.tableMerge || lastState?.metadata?.tableOperation) {
			const timeSinceMerge = Date.now() - (lastState.timestamp || 0)
			
			if (timeSinceMerge < 2000) {
				return
			}
		}
		
		const { isUndoRedoInProgress, isFunctionActive, cellTarget } = storeToRefs(useEditorStore())
		if (isUndoRedoInProgress.value) {
			return
		}
		if (isFunctionActive.value || cellTarget.value?.dataset?.type === 'function') {
			return
		}
		
		await this.history.push()
		this.lastSaveTime = Date.now()
	}

	flush(): void {
		this.clearTimer()
		const { isUndoRedoInProgress } = storeToRefs(useEditorStore())
		if (isUndoRedoInProgress.value) return
		this.history.push(true)
		this.lastSaveTime = Date.now()
		this.lastInputMode = null
		this.lastDeleteDirection = null
	}

	cleanup(): void {
		this.clearTimer()
		this.lastInputMode = null
		this.lastDeleteDirection = null
		this.lastSaveTime = 0
	}
}
